import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, FieldError, Input, TextField } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils';
import type { VariantBarcodesEditor } from './useVariantBarcodesForm';
import type { AlternateBarcodeDraft } from './variantBarcodesFormMeta';

type VariantBarcodeAlternateProps = {
    alternate: AlternateBarcodeDraft;
    editor: VariantBarcodesEditor;
};

/**
 * One alternate barcode in the drawer: its field, with the error under it,
 * then **Make main** and **Remove**, each named for its code for screen
 * readers.
 */
export const VariantBarcodeAlternate: React.FC<
    VariantBarcodeAlternateProps
> = ({ alternate, editor }) => {
    const { t } = useTranslation('pages');
    const {
        errors,
        isSaving,
        inputRefOf,
        onAlternateChange,
        onFieldBlur,
        onMakeMain,
        onRemove,
    } = editor;
    const code = alternate.value.trim();
    const error = errors[alternate.key];

    return (
        <li
            className="flex flex-wrap items-start gap-2"
            {...getNodeTestIdProp(
                `variant-barcodes-alternate-${alternate.key}`,
            )}
        >
            <TextField
                aria-label={t('products.detail.barcodes.alternate')}
                className="flex min-w-0 flex-1 basis-48 flex-col gap-1"
                isDisabled={isSaving}
                isInvalid={error !== undefined}
                onBlur={() => onFieldBlur(alternate.key)}
                onChange={(value) => onAlternateChange(alternate.key, value)}
                value={alternate.value}
            >
                <Input ref={inputRefOf(alternate.key)} />
                <FieldError>
                    {error === undefined ? null : t(error.key, error.values)}
                </FieldError>
            </TextField>
            <div className="flex gap-1">
                <Button
                    aria-label={t('products.detail.barcodes.makeMainAria', {
                        barcode: code,
                    })}
                    isDisabled={isSaving}
                    onPress={() => onMakeMain(alternate.key)}
                    size="md"
                    variant="ghost"
                    {...getNodeTestIdProp(
                        `variant-barcodes-make-main-${alternate.key}`,
                    )}
                >
                    {t('products.detail.barcodes.makeMain')}
                </Button>
                <Button
                    aria-label={t('products.detail.barcodes.removeAria', {
                        barcode: code,
                    })}
                    isDisabled={isSaving}
                    onPress={() => onRemove(alternate.key)}
                    size="md"
                    variant="ghost"
                    {...getNodeTestIdProp(
                        `variant-barcodes-remove-${alternate.key}`,
                    )}
                >
                    {t('products.detail.barcodes.remove')}
                </Button>
            </div>
        </li>
    );
};
